import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { 
  Eye, 
  EyeOff, 
  ArrowRight, 
  Loader2, 
  Shield, 
  Heart, 
  Users, 
  Sparkles,
  CheckCircle,
  Bot,
  Clock,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_caregiver-hub-25/artifacts/7i9n9nvr_Zorg%20Vleugel%20Logo%20Icoon%20Rood.png";

// Feature pills for mobile
const FEATURES = [
  { id: 'ai', icon: Bot, text: "AI Assistent", gradient: "from-violet-500 to-purple-600" },
  { id: 'clients', icon: Users, text: "Cliëntbeheer", gradient: "from-blue-500 to-cyan-600" },
  { id: 'tasks', icon: Clock, text: "Taakplanning", gradient: "from-emerald-500 to-teal-600" },
  { id: 'protocols', icon: Shield, text: "Protocollen", gradient: "from-amber-500 to-orange-600" },
];

// Trust badges
const TRUST_BADGES = [
  "Veilig inloggen",
  "Geen 2FA nodig",
  "Direct starten",
];

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [focusedField, setFocusedField] = useState(null);
  
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(email, password);
      toast.success('Welkom terug!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Inloggen mislukt');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-gradient-to-br from-slate-50 via-blue-50/30 to-violet-50/30 relative overflow-hidden">
      {/* Decorative Background Elements */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-violet-300/30 to-purple-300/30 rounded-full blur-3xl" />
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-blue-300/30 to-cyan-300/30 rounded-full blur-3xl" />
        <div className="absolute top-1/3 left-1/2 w-96 h-96 bg-gradient-to-br from-rose-200/20 to-orange-200/20 rounded-full blur-3xl" />
      </div>

      {/* Left Side - Branding (Desktop Only) */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          {/* Animated gradient orbs */}
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-gradient-to-br from-violet-500/30 to-purple-500/30 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-1/4 left-1/4 w-48 h-48 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
          <div className="absolute top-1/2 right-1/3 w-32 h-32 bg-gradient-to-br from-amber-500/20 to-orange-500/20 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '2s' }} />
        </div>
        
        <div className="relative z-10 flex flex-col justify-between p-12 xl:p-16 w-full">
          {/* Logo */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="flex items-center gap-4"
          >
            <div className="w-14 h-14 rounded-2xl bg-white/10 backdrop-blur-sm flex items-center justify-center border border-white/20">
              <img src={LOGO_URL} alt="Zorg-Vleugel" className="w-10 h-10" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Zorg-Vleugel</h2>
              <p className="text-white/50 text-sm">Begeleid Wonen</p>
            </div>
          </motion.div>

          {/* Main Content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
            className="space-y-8"
          >
            <div>
              <h1 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
                Jouw digitale
                <br />
                <span className="bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 bg-clip-text text-transparent">
                  zorgassistent
                </span>
              </h1>
              <p className="text-white/60 text-lg mt-6 max-w-md leading-relaxed">
                Alles voor je dienst in één dashboard. Protocollen, cliëntinfo, 
                planning en een AI die je vragen direct beantwoordt.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-2 gap-4">
              {FEATURES.map((feature) => (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.4, duration: 0.4 }}
                  className="group p-4 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 hover:border-white/20 transition-all cursor-default"
                >
                  <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-3 shadow-lg`}>
                    <feature.icon className="w-5 h-5 text-white" />
                  </div>
                  <span className="text-white/90 font-medium">{feature.text}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8 }}
            className="flex items-center gap-6 text-white/40 text-sm"
          >
            <span>© 2026 Zorg-Vleugel</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>Begeleid Wonen Dashboard</span>
          </motion.div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="flex-1 flex flex-col min-h-screen lg:min-h-0 relative z-10">
        {/* Mobile Header */}
        <motion.header 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="lg:hidden px-6 pt-8 pb-4"
        >
          <div className="flex items-center justify-center gap-3">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center shadow-lg">
              <img src={LOGO_URL} alt="Zorg-Vleugel" className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Zorg-Vleugel</h2>
              <p className="text-slate-500 text-xs">Begeleid Wonen</p>
            </div>
          </div>
        </motion.header>

        {/* Login Form Container */}
        <div className="flex-1 flex items-center justify-center px-6 py-8 lg:py-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-sm"
          >
            {/* Welcome Text */}
            <div className="text-center mb-8">
              <motion.h1 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2"
              >
                Welkom terug
              </motion.h1>
              <motion.p 
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="text-slate-500"
              >
                Log in om je dienst te starten
              </motion.p>
            </div>

            {/* Login Form */}
            <motion.form 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              onSubmit={handleLogin} 
              className="space-y-5"
            >
              {/* Email Field */}
              <div className="space-y-2">
                <label className="text-sm font-semibold text-slate-700 block">
                  E-mailadres
                </label>
                <div className={`relative rounded-2xl transition-all duration-300 ${
                  focusedField === 'email' 
                    ? 'ring-2 ring-violet-500/50 shadow-lg shadow-violet-500/10' 
                    : ''
                }`}>
                  <Input
                    type="email"
                    placeholder="naam@zorg-vleugel.nl"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => setFocusedField(null)}
                    required
                    data-testid="login-email-input"
                    className="h-14 px-5 bg-white/80 backdrop-blur-sm border-slate-200/80 rounded-2xl text-base focus:border-violet-400 focus:ring-0 transition-all"
                  />
                </div>
              </div>
              
              {/* Password Field */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold text-slate-700">
                    Wachtwoord
                  </label>
                  <Link 
                    to="/forgot-password" 
                    className="text-sm font-medium text-violet-600 hover:text-violet-700 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Vergeten?
                  </Link>
                </div>
                <div className={`relative rounded-2xl transition-all duration-300 ${
                  focusedField === 'password' 
                    ? 'ring-2 ring-violet-500/50 shadow-lg shadow-violet-500/10' 
                    : ''
                }`}>
                  <Input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => setFocusedField(null)}
                    required
                    data-testid="login-password-input"
                    className="h-14 px-5 pr-14 bg-white/80 backdrop-blur-sm border-slate-200/80 rounded-2xl text-base focus:border-violet-400 focus:ring-0 transition-all"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-all"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button 
                  type="submit" 
                  className="w-full h-14 rounded-2xl text-base font-semibold bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 hover:from-slate-900 hover:via-black hover:to-slate-900 shadow-xl shadow-slate-900/20 transition-all duration-300"
                  disabled={loading}
                  data-testid="login-submit-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Inloggen
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </motion.div>
            </motion.form>

            {/* Trust Badges */}
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="flex items-center justify-center gap-4 mt-8 flex-wrap"
            >
              {TRUST_BADGES.map((badge) => (
                <div key={badge} className="flex items-center gap-1.5 text-xs text-slate-500">
                  <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                  <span>{badge}</span>
                </div>
              ))}
            </motion.div>
          </motion.div>
        </div>

        {/* Mobile Feature Pills */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
          className="lg:hidden px-6 pb-8"
        >
          <div className="flex gap-2 overflow-x-auto scrollbar-hide pb-2 justify-center flex-wrap">
            {FEATURES.map((feature) => (
              <div
                key={feature.id}
                className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/80 backdrop-blur-sm border border-slate-200/50 shadow-sm"
              >
                <div className={`w-7 h-7 rounded-lg bg-gradient-to-br ${feature.gradient} flex items-center justify-center`}>
                  <feature.icon className="w-3.5 h-3.5 text-white" />
                </div>
                <span className="text-sm font-medium text-slate-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Mobile Footer */}
        <motion.footer 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
          className="lg:hidden text-center pb-6 text-xs text-slate-400"
        >
          © 2026 Zorg-Vleugel • Begeleid Wonen
        </motion.footer>
      </div>
    </div>
  );
}
